'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { isAuthenticated, getAuthUser, type AuthUser } from '@/lib/apollo-client-simple';

const navLinks = [
  { label: 'Shop', href: '/products' },
  { label: 'Skincare', href: '/products?category=skincare' },
  { label: 'Sale', href: '/products?onSale=true' },
  { label: 'New', href: '/products?sort=Newest' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [authUser, setAuthUserState] = useState<AuthUser | null>(null);
  const { itemCount: cartCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const pathname = usePathname();
  const router = useRouter();

  // Auto-focus the input when the search drawer opens so the user can type immediately.
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  // Close the search drawer whenever the route changes so submit → navigate collapses it.
  useEffect(() => {
    setSearchOpen(false);
  }, [pathname]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    router.push(`/products?search=${encodeURIComponent(q)}`);
  };

  // Track logged-in state on the client — server render has no localStorage
  // access, so we always start "logged out" then upgrade on hydration.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isAuthenticated()) {
      setAuthUserState(getAuthUser());
    }
    const onStorage = () => {
      setAuthUserState(isAuthenticated() ? getAuthUser() : null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [pathname]);

  const accountLabel = authUser
    ? (authUser.firstName || authUser.displayName || authUser.username || 'Account')
    : 'Account';
  const accountHref = authUser ? '/account' : '/sign-up-login';

  // Only use transparent/hero style on the home page
  const isHeroPage = pathname === '/';
  const solidStyle = !isHeroPage || scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <div className="fixed top-0 left-0 w-full z-50">
        {/* Free shipping banner */}
        <Link
          href="/products"
          className="block bg-primary text-primary-foreground text-center py-2 px-4 text-[10px] uppercase tracking-[0.3em] font-medium hover:bg-primary/90 transition-colors"
        >
          Free shipping on orders over $75 USD / <span className="font-sans">J$</span>12,000 — Use code <span className="text-accent font-bold">SWEETSKYN</span>
        </Link>
        <header
          className={`w-full transition-all duration-500 ${
            solidStyle
              ? 'bg-background/95 backdrop-blur-xl border-b border-border shadow-sm'
              : 'bg-transparent'
          }`}
        >
          <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <AppLogo size={36} onClick={undefined} />
              <span
                data-ocwp-field="brandName"
                className={`font-display text-xl font-semibold tracking-tight transition-colors duration-300 ${
                  solidStyle ? 'text-foreground' : 'text-white'
                }`}
              >
                SweetSkyn by Kym
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks?.map((link) => (
                <Link
                  key={link?.label}
                  href={link?.href}
                  className={`text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 hover:text-accent ${
                    solidStyle ? 'text-foreground' : 'text-white'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              <Link
                href={accountHref}
                className={`hidden md:flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 hover:text-accent ${
                  solidStyle ? 'text-foreground' : 'text-white'
                }`}
              >
                <Icon name="UserIcon" size={18} className="flex-shrink-0" />
                <span>{accountLabel}</span>
              </Link>

              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                className={`p-2 transition-colors duration-300 hover:text-accent ${
                  solidStyle ? 'text-foreground' : 'text-white/90'
                } ${searchOpen ? 'text-accent' : ''}`}
                aria-label="Search products"
                aria-expanded={searchOpen}
              >
                <Icon name={searchOpen ? 'XMarkIcon' : 'MagnifyingGlassIcon'} size={20} />
              </button>

              <Link
                href="/account?tab=wishlist"
                className={`relative p-2 transition-colors duration-300 hover:text-accent ${
                  solidStyle ? 'text-foreground' : 'text-white/90'
                }`}
                aria-label={`Wishlist, ${wishlistCount} items`}
              >
                <Icon name="HeartIcon" size={20} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-accent text-accent-foreground text-[9px] font-bold rounded-full flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              <Link
                href="/checkout"
                className={`relative p-2 transition-colors duration-300 hover:text-accent ${
                  solidStyle ? 'text-foreground' : 'text-white/90'
                }`}
                aria-label={`Shopping bag, ${cartCount} items`}
              >
                <Icon name="ShoppingBagIcon" size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-accent text-accent-foreground text-[9px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile hamburger */}
              <button
                className={`md:hidden p-2 transition-colors duration-300 ${
                  solidStyle ? 'text-foreground' : 'text-white'
                }`}
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <Icon name="Bars3Icon" size={22} />
              </button>
            </div>
          </nav>

          {/* Search drawer — full-width row below the nav, collapses via max-height transition.
              Always solid background when open so the input text is legible regardless
              of whether the header above it is transparent (hero) or solid. */}
          <div
            className={`overflow-hidden border-t transition-all duration-300 ease-out ${
              searchOpen
                ? 'max-h-24 border-border bg-background shadow-lg'
                : 'max-h-0 border-transparent'
            }`}
          >
            <form onSubmit={handleSearchSubmit} className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3">
              <Icon name="MagnifyingGlassIcon" size={18} className="text-muted-foreground flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search body butters, scents, scrubs…"
                className="flex-1 bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none min-w-0"
              />
              <button
                type="submit"
                className="text-[10px] font-bold uppercase tracking-widest text-foreground border border-border px-4 py-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors flex-shrink-0"
              >
                Search
              </button>
            </form>
          </div>
        </header>
      </div>
      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-primary flex flex-col justify-center items-center transition-transform duration-500 ${
          mobileOpen ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <button
          className="absolute top-6 right-6 p-2 text-primary-foreground"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          <Icon name="XMarkIcon" size={28} />
        </button>

        <div className="flex flex-col gap-8 text-center">
          {navLinks?.map((link) => (
            <Link
              key={link?.label}
              href={link?.href}
              onClick={() => setMobileOpen(false)}
              className="font-display text-5xl text-primary-foreground tracking-tight hover:text-accent transition-colors"
            >
              {link?.label}
            </Link>
          ))}
          <Link
            href={accountHref}
            onClick={() => setMobileOpen(false)}
            className="font-display text-5xl text-primary-foreground tracking-tight hover:text-accent transition-colors"
          >
            {accountLabel}
          </Link>
          <Link
            href="/checkout"
            onClick={() => setMobileOpen(false)}
            className="font-display text-5xl text-primary-foreground tracking-tight hover:text-accent transition-colors"
          >
            Bag ({cartCount})
          </Link>
        </div>
      </div>
    </>
  );
}